import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { SocialAccountsTable } from './components/social-accounts-table/social-accounts-table';
import type { SocialAccountData } from './components/social-accounts-table/social-accounts-table-types';
import type { SocialAccountsColumn } from './components/social-accounts-table/social-accounts-column-dialog';
import { useSocialAccountsTable } from './components/social-accounts-table/hooks/use-social-accounts-table';
import { useSocialAccountsColumnDialog } from './components/social-accounts-table/hooks/use-social-accounts-column-dialog';
import { SocialAccountsColumnDialog } from './components/social-accounts-table/social-accounts-column-dialog';
import { socialAccountsTableColumns } from './components/social-accounts-table/social-accounts-table-columns';
import { sharedRenderRowSocialAccounts } from './components/social-accounts/shared-render-row-social-accounts';

interface SocialAdsSocialAccountsProps {
  isLoading?: boolean;
  isRefreshing?: boolean;
  data: SocialAccountData[];
  selectedColumns: SocialAccountsColumn[];
  setSelectedColumns: (
    columns: SocialAccountsColumn[] | ((prev: SocialAccountsColumn[]) => SocialAccountsColumn[]),
  ) => void;
  onSaveColumns?: (columns: SocialAccountsColumn[]) => Promise<void>;
}

// Type for the extended column definition with rowType
type SocialAccountsTableColumn = ColumnDef<SocialAccountData> & {
  rowType: string;
  accessorKey: keyof SocialAccountData;
  id?: string;
};

const SocialAdsSocialAccounts: React.FC<SocialAdsSocialAccountsProps> = ({
  isLoading = false,
  isRefreshing = false,
  data,
  selectedColumns,
  setSelectedColumns,
  onSaveColumns,
}) => {
  const { socialAccountsTableProps } = useSocialAccountsTable({
    data,
    isLoading,
    isRefreshing,
    selectedColumns,
    setSelectedColumns,
  });

  // Use the column dialog hook
  const { columnDialogProps, openDialog } = useSocialAccountsColumnDialog({
    selectedColumns,
    setSelectedColumns,
    onSave: onSaveColumns,
  });

  const columns = useMemo(() => {
    // Create a map of column definitions by ID for easy lookup
    const columnMap = new Map<string, SocialAccountsTableColumn>();
    socialAccountsTableColumns.forEach((col) => {
      const typedCol = col as SocialAccountsTableColumn;
      const id = typedCol.id ?? typedCol.accessorKey;
      columnMap.set(id, { ...typedCol, id });
    });

    // Get column order from selectedColumns, sorted by order property (without mutating)
    const sortedColumns = [...selectedColumns].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Filter to only show non-hidden columns
    const visibleColumns = sortedColumns.filter((col) => !col.hidden);

    // Get column order for visible columns only
    const columnOrder = visibleColumns.map((col) => col.id);

    // Process columns based on the current column order
    return columnOrder
      .map((columnId) => {
        const col = columnMap.get(columnId);
        if (!col) {
          console.warn(`Column with ID ${columnId} not found in column definitions`);
          return null;
        }

        const accessorKey = col.accessorKey;
        const id = col.id ?? accessorKey;
        const rowType = col.rowType ?? accessorKey;

        return {
          ...col,
          id,
          // Remove any tooltip property from meta to prevent DataTable from rendering tooltips
          meta: {
            ...col.meta,
            tooltip: undefined, // Explicitly remove tooltip to prevent overlay issues
          },
          header: () => (
            <span
              className={[
                'flex',
                rowType === 'impressions' ||
                rowType === 'reach' ||
                rowType === 'clicks' ||
                rowType === 'spend' ||
                rowType === 'sales' ||
                rowType === 'ctr' ||
                rowType === 'cpm' ||
                rowType === 'cpc'
                  ? 'justify-end'
                  : '',
                'font-medium text-gray-900 text-[14px]',
              ].join(' ')}
            >
              {col.header}
            </span>
          ),
          cell: ({ row }) => {
            if (isRefreshing) {
              return <div className="h-4 rounded bg-gray-100 animate-pulse w-full" />;
            }
            return sharedRenderRowSocialAccounts(rowType, row, col, false);
          },
        };
      })
      .filter(Boolean) as ColumnDef<SocialAccountData>[];
  }, [selectedColumns, isRefreshing]);

  return (
    <div className="h-full flex flex-col">
      {/* Main Content - Social Accounts Table - Extends to fill remaining space */}
      <div className="flex-1 min-h-0">
        <SocialAccountsTable
          {...socialAccountsTableProps}
          columns={columns}
          draggable
          onColumnDragEnd={socialAccountsTableProps.handleColumnDragEnd}
          onOpenColumnDialog={openDialog}
        />
      </div>

      {/* Column Dialog */}
      <SocialAccountsColumnDialog {...columnDialogProps} />
    </div>
  );
};

export default SocialAdsSocialAccounts;
